/** BMI and WHtR calculations — mirrors backend health_metrics.py */

export const HEIGHT_CM_MIN = 50;
export const HEIGHT_CM_MAX = 300;
export const WEIGHT_KG_MIN = 10;
export const WEIGHT_KG_MAX = 500;
export const WAIST_CM_MIN = 20;
export const WAIST_CM_MAX = 250;

export const BMI_STATUS = {
  underweight: { label: 'Underweight', color: '#4dabf7' },
  normal_weight: { label: 'Normal Weight', color: '#4cceac' },
  overweight: { label: 'Overweight', color: '#f5a623' },
  obesity_class_i: { label: 'Obesity Class I', color: '#e53935' },
  obesity_class_ii: { label: 'Obesity Class II', color: '#e53935' },
  obesity_class_iii: { label: 'Obesity Class III', color: '#e53935' },
};

export const WHTR_STATUS = {
  underweight_risk: {
    label: 'Underweight / Possible Nutritional Risk',
    color: '#4dabf7',
    interpretation:
      'Waist circumference is low relative to height. Possible nutritional risk — consider further assessment.',
  },
  healthy: {
    label: 'Healthy',
    color: '#4cceac',
    interpretation:
      'Your waist circumference is within the recommended range relative to your height.',
  },
  increased_risk: {
    label: 'Increased Risk',
    color: '#f5a623',
    interpretation:
      'Waist circumference is above the healthy range relative to height. Increased health risk — lifestyle changes may be beneficial.',
  },
  high_risk: {
    label: 'High Risk',
    color: '#e53935',
    interpretation:
      'Waist circumference is substantially above the recommended range relative to height. High health risk — medical follow-up is advised.',
  },
};

export const validateHeightCm = (heightCm) => {
  if (heightCm === '' || heightCm == null) return 'Height is required';
  const value = parseFloat(heightCm);
  if (Number.isNaN(value)) return 'Height must be a number';
  if (value <= 0) return 'Height must be greater than 0';
  if (value < HEIGHT_CM_MIN || value > HEIGHT_CM_MAX) {
    return `Height must be between ${HEIGHT_CM_MIN} and ${HEIGHT_CM_MAX} cm`;
  }
  return null;
};

export const validateWeightKg = (weightKg) => {
  if (weightKg === '' || weightKg == null) return 'Weight is required';
  const value = parseFloat(weightKg);
  if (Number.isNaN(value)) return 'Weight must be a number';
  if (value <= 0) return 'Weight must be greater than 0';
  if (value < WEIGHT_KG_MIN || value > WEIGHT_KG_MAX) {
    return `Weight must be between ${WEIGHT_KG_MIN} and ${WEIGHT_KG_MAX} kg`;
  }
  return null;
};

export const validateWaistCm = (waistCm) => {
  if (waistCm === '' || waistCm == null) return 'Waist circumference is required';
  const value = parseFloat(waistCm);
  if (Number.isNaN(value)) return 'Waist circumference must be a number';
  if (value <= 0) return 'Waist circumference must be greater than 0';
  if (value < WAIST_CM_MIN || value > WAIST_CM_MAX) {
    return `Waist must be between ${WAIST_CM_MIN} and ${WAIST_CM_MAX} cm`;
  }
  return null;
};

export const calculateBmi = (weightKg, heightCm) => {
  const w = parseFloat(weightKg);
  const h = parseFloat(heightCm);
  if (Number.isNaN(w) || Number.isNaN(h) || h <= 0 || w <= 0) return null;
  const heightM = h / 100;
  return Math.round((w / (heightM * heightM)) * 100) / 100;
};

export const calculateWaistHeightRatio = (waistCm, heightCm) => {
  const w = parseFloat(waistCm);
  const h = parseFloat(heightCm);
  if (Number.isNaN(w) || Number.isNaN(h) || h <= 0 || w <= 0) return null;
  return Math.round((w / h) * 100) / 100;
};

export const classifyBmi = (bmi) => {
  if (bmi == null) return null;
  if (bmi < 18.5) return 'underweight';
  if (bmi <= 24.9) return 'normal_weight';
  if (bmi <= 29.9) return 'overweight';
  if (bmi <= 34.9) return 'obesity_class_i';
  if (bmi <= 39.9) return 'obesity_class_ii';
  return 'obesity_class_iii';
};

export const classifyWhtr = (whtr) => {
  if (whtr == null) return null;
  if (whtr < 0.4) return 'underweight_risk';
  if (whtr <= 0.49) return 'healthy';
  if (whtr <= 0.59) return 'increased_risk';
  return 'high_risk';
};

export const recommendedMaxWaistCm = (heightCm) => {
  const error = validateHeightCm(heightCm);
  if (error) return null;
  return Math.round(parseFloat(heightCm) / 2 * 10) / 10;
};

export const calculateBmiResult = (weightKg, heightCm) => {
  const heightError = validateHeightCm(heightCm);
  const weightError = validateWeightKg(weightKg);
  if (heightError || weightError) {
    return { valid: false, errors: { height_cm: heightError, weight_kg: weightError } };
  }
  const bmi = calculateBmi(weightKg, heightCm);
  const category = classifyBmi(bmi);
  return {
    valid: true,
    bmi,
    category,
    status: BMI_STATUS[category]?.label,
    color: BMI_STATUS[category]?.color,
  };
};

export const calculateWhtrResult = (waistCm, heightCm) => {
  const heightError = validateHeightCm(heightCm);
  const waistError = validateWaistCm(waistCm);
  if (heightError || waistError) {
    return { valid: false, errors: { height_cm: heightError, waist_cm: waistError } };
  }
  const whtr = calculateWaistHeightRatio(waistCm, heightCm);
  const category = classifyWhtr(whtr);
  const meta = WHTR_STATUS[category] || {};
  return {
    valid: true,
    whtr,
    category,
    status: meta.label,
    color: meta.color,
    interpretation: meta.interpretation,
    recommendedMaxWaistCm: recommendedMaxWaistCm(heightCm),
  };
};

export const formatBmiCategory = (key) => BMI_STATUS[key]?.label || key?.replace(/_/g, ' ') || '—';
export const formatWhtrCategory = (key) => WHTR_STATUS[key]?.label || key?.replace(/_/g, ' ') || '—';

export const calculateHealthRisk = (health) => {
  const bmiResult = calculateBmiResult(health?.weight_kg, health?.height_cm);
  const whtrResult = calculateWhtrResult(health?.waist_cm, health?.height_cm);
  if (whtrResult.valid && whtrResult.category === 'high_risk') return 'high';
  if (whtrResult.valid && whtrResult.category === 'increased_risk') return 'moderate';
  if (bmiResult.valid && ['obesity_class_i', 'obesity_class_ii', 'obesity_class_iii'].includes(bmiResult.category)) {
    return 'high';
  }
  if (bmiResult.valid && bmiResult.category === 'overweight') return 'moderate';
  return 'low';
};
